import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from './config';
import { formatLocalDateTime } from './timezone';

const SOURCE_ICONS = {
  generic: '🔗',
  gmail: '📧',
  outlook: '📬',
  form: '📋',
};

const STATUS_COLORS = {
  published: 'var(--success-color)',
  draft: 'var(--warning-color)',
  paused: 'var(--text-tertiary)',
};

const containerStyle = {
  width: '100%',
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  background: 'var(--bg-primary)',
  color: 'var(--text-primary)',
  boxSizing: 'border-box',
  overflow: 'hidden',
};

const headerStyle = {
  padding: '16px 24px',
  borderBottom: '1px solid var(--border-color)',
  background: 'var(--bg-secondary)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexShrink: 0,
};

const contentStyle = {
  flex: 1,
  overflowY: 'auto',
  padding: '24px',
};

const cardStyle = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '12px',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '16px',
};

const btn = (bg = 'var(--accent-color)', small = false) => ({
  padding: small ? '4px 10px' : '8px 16px',
  background: bg,
  color: 'var(--text-inverse)',
  border: 'none',
  borderRadius: '4px',
  cursor: 'pointer',
  fontSize: small ? '12px' : '13px',
  fontWeight: '500',
  whiteSpace: 'nowrap',
});

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border-color)',
  borderRadius: '4px',
  background: 'var(--bg-input)',
  color: 'var(--text-primary)',
  fontSize: '13px',
  boxSizing: 'border-box',
  fontFamily: 'monospace',
};

const textareaStyle = {
  ...inputStyle,
  minHeight: '80px',
  resize: 'vertical',
};

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.55)',
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const modalStyle = {
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderRadius: '8px',
  padding: '24px',
  width: '700px',
  maxWidth: '95vw',
  maxHeight: '85vh',
  overflowY: 'auto',
  boxSizing: 'border-box',
};

const thStyle = {
  textAlign: 'left',
  padding: '8px 12px',
  borderBottom: '1px solid var(--border-color)',
  color: 'var(--text-secondary)',
  fontSize: '11px',
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  fontWeight: '600',
};

const tdStyle = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--border-color)',
  fontSize: '12px',
  verticalAlign: 'top',
  wordBreak: 'break-all',
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button
      onClick={handleCopy}
      style={{ ...btn('var(--bg-tertiary)', true), color: 'var(--text-primary)', marginLeft: '6px' }}
      title="Copy to clipboard"
    >
      {copied ? '✓' : '📋'}
    </button>
  );
}

function LogModal({ entry, onClose, token }) {
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/webhooks/log?workflow_id=${encodeURIComponent(entry.workflow_id)}&slug=${encodeURIComponent(entry.slug)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => { setLogs(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [entry.workflow_id, entry.slug, token]);

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>
            {SOURCE_ICONS[entry.source] || '🔗'} Payload Log — {entry.workflow_name}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--text-secondary)' }}>✕</button>
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '12px', fontFamily: 'monospace' }}>
          {entry.url}
        </div>

        {loading && <div style={{ color: 'var(--text-tertiary)', padding: '20px 0' }}>Loading…</div>}
        {!loading && logs && logs.length === 0 && (
          <div style={{ color: 'var(--text-tertiary)', padding: '20px 0', textAlign: 'center' }}>
            No payload logs yet. Trigger this webhook to start recording.
          </div>
        )}
        {!loading && logs && logs.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Method</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Latency</th>
                <th style={thStyle}>Body</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <React.Fragment key={log.id}>
                  <tr
                    style={{ cursor: 'pointer' }}
                    onClick={() => toggle(log.id)}
                  >
                    <td style={tdStyle}>{log.triggered_at ? formatLocalDateTime(log.triggered_at) : '—'}</td>
                    <td style={tdStyle}>{log.method || '—'}</td>
                    <td style={{ ...tdStyle, color: log.response_status >= 400 ? 'var(--error-color)' : 'var(--success-color)' }}>
                      {log.response_status ?? '—'}
                    </td>
                    <td style={tdStyle}>{log.latency_ms != null ? `${log.latency_ms} ms` : '—'}</td>
                    <td style={tdStyle}>
                      {log.body != null
                        ? <span style={{ color: 'var(--text-secondary)' }}>{expanded[log.id] ? '▲' : '▶'} Details</span>
                        : '—'}
                    </td>
                  </tr>
                  {expanded[log.id] && (
                    <tr>
                      <td colSpan={5} style={{ ...tdStyle, background: 'var(--bg-primary)', padding: '12px 16px' }}>
                        {log.query_params && Object.keys(log.query_params).length > 0 && (
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 600 }}>Query Params</div>
                            <pre style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(log.query_params, null, 2)}
                            </pre>
                          </div>
                        )}
                        {log.headers && Object.keys(log.headers).length > 0 && (
                          <div style={{ marginBottom: '10px' }}>
                            <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 600 }}>Headers</div>
                            <pre style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(log.headers, null, 2)}
                            </pre>
                          </div>
                        )}
                        <div>
                          <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginBottom: '4px', textTransform: 'uppercase', fontWeight: 600 }}>Body</div>
                          <pre style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>
                            {log.body != null ? JSON.stringify(log.body, null, 2) : '(empty)'}
                          </pre>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function TestModal({ entry, onClose, token }) {
  const [method, setMethod] = useState('POST');
  const [headersJson, setHeadersJson] = useState('{}');
  const [bodyJson, setBodyJson] = useState('{}');
  const [queryJson, setQueryJson] = useState('{}');
  const [firing, setFiring] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const fire = async () => {
    setFiring(true);
    setResult(null);
    setError(null);
    let headers = {}, body = {}, query = {};
    try {
      headers = JSON.parse(headersJson || '{}');
      body = JSON.parse(bodyJson || '{}');
      query = JSON.parse(queryJson || '{}');
    } catch (parseErr) {
      setError('Invalid JSON in headers, body, or query params.');
      setFiring(false);
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/webhooks/test-fire`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          workflow_id: entry.workflow_id,
          slug: entry.slug,
          method,
          headers,
          body,
          query_params: query,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || JSON.stringify(data));
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setFiring(false);
    }
  };

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ margin: 0, fontSize: '15px' }}>
            🧪 Test — {entry.workflow_name}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--text-secondary)' }}>✕</button>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Method</label>
          <select
            value={method}
            onChange={e => setMethod(e.target.value)}
            style={{ ...inputStyle, fontFamily: 'inherit' }}
          >
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(m => <option key={m}>{m}</option>)}
          </select>
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Headers (JSON)</label>
          <textarea
            value={headersJson}
            onChange={e => setHeadersJson(e.target.value)}
            style={textareaStyle}
            spellCheck={false}
          />
        </div>

        <div style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Body (JSON)</label>
          <textarea
            value={bodyJson}
            onChange={e => setBodyJson(e.target.value)}
            style={{ ...textareaStyle, minHeight: '120px' }}
            spellCheck={false}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Query Params (JSON)</label>
          <textarea
            value={queryJson}
            onChange={e => setQueryJson(e.target.value)}
            style={textareaStyle}
            spellCheck={false}
          />
        </div>

        <button onClick={fire} disabled={firing} style={{ ...btn(), opacity: firing ? 0.6 : 1 }}>
          {firing ? 'Firing…' : '🚀 Send Test Request'}
        </button>

        {error && (
          <div style={{ marginTop: '12px', padding: '10px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid var(--error-color)', borderRadius: '4px', color: 'var(--error-color)', fontSize: '13px' }}>
            {error}
          </div>
        )}
        {result && (
          <div style={{ marginTop: '12px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '6px', textTransform: 'uppercase', fontWeight: 600 }}>Response</div>
            <pre style={{ margin: 0, fontSize: '12px', color: 'var(--success-color)', background: 'var(--bg-primary)', padding: '10px', borderRadius: '4px', whiteSpace: 'pre-wrap', maxHeight: '200px', overflowY: 'auto' }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

export default function WebhookDashboard({ onBack }) {
  const token = localStorage.getItem('token');
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [logEntry, setLogEntry] = useState(null);
  const [testEntry, setTestEntry] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/webhooks/registry`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(data => { setWebhooks(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <button onClick={onBack} style={{ ...btn('var(--bg-tertiary)', true), color: 'var(--text-primary)', fontSize: '13px' }}>← Back</button>
        <span style={{ fontSize: '20px' }}>🔗</span>
        <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>Webhook Registry</h2>
        <span style={{ fontSize: '13px', color: 'var(--text-tertiary)', marginLeft: '4px' }}>
          {!loading && `${webhooks.length} webhook${webhooks.length !== 1 ? 's' : ''}`}
        </span>
        <div style={{ flex: 1 }} />
        <button onClick={load} style={{ ...btn('var(--bg-tertiary)', true), color: 'var(--text-primary)' }}>↻ Refresh</button>
      </div>

      <div style={contentStyle}>
        {loading && (
          <div style={{ color: 'var(--text-tertiary)', padding: '40px', textAlign: 'center' }}>Loading webhooks…</div>
        )}
        {!loading && webhooks.length === 0 && (
          <div style={{ color: 'var(--text-tertiary)', padding: '60px', textAlign: 'center', fontSize: '14px' }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}>🔗</div>
            No webhook triggers found. Add a Webhook node to a workflow to get started.
          </div>
        )}
        {!loading && webhooks.map((wh) => (
          <div key={`${wh.workflow_id}-${wh.slug}`} style={cardStyle}>
            <div style={{ fontSize: '28px', lineHeight: 1, flexShrink: 0, marginTop: '2px' }}>
              {SOURCE_ICONS[wh.source] || '🔗'}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: '14px' }}>{wh.workflow_name}</span>
                <span style={{
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  background: 'var(--bg-tertiary)',
                  color: STATUS_COLORS[wh.status] || 'var(--text-secondary)',
                  border: `1px solid ${STATUS_COLORS[wh.status] || 'var(--border-color)'}`,
                }}>
                  {wh.status}
                </span>
                <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', background: 'var(--bg-tertiary)', padding: '2px 8px', borderRadius: '10px' }}>
                  {wh.source}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                <span style={{ fontSize: '11px', fontFamily: 'monospace', color: 'var(--text-secondary)', background: 'var(--bg-primary)', padding: '2px 6px', borderRadius: '3px', border: '1px solid var(--border-color)' }}>
                  {wh.method}
                </span>
                <span style={{ fontSize: '12px', fontFamily: 'monospace', color: 'var(--text-secondary)', wordBreak: 'break-all' }}>
                  {wh.url}
                </span>
                <CopyButton text={wh.url} />
              </div>

              <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                <span>
                  <strong style={{ color: 'var(--text-secondary)' }}>{wh.trigger_count}</strong> trigger{wh.trigger_count !== 1 ? 's' : ''}
                </span>
                {wh.last_triggered_at && (
                  <span>
                    Last: <strong style={{ color: 'var(--text-secondary)' }}>{formatLocalDateTime(wh.last_triggered_at)}</strong>
                  </span>
                )}
                {!wh.last_triggered_at && (
                  <span>Never triggered</span>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <button
                onClick={() => setLogEntry(wh)}
                style={{ ...btn('var(--bg-tertiary)', true), color: 'var(--text-primary)' }}
              >
                📋 Logs
              </button>
              {(wh.source === 'generic' || wh.source === 'form') && (
                <button
                  onClick={() => setTestEntry(wh)}
                  style={{ ...btn('var(--accent-color)', true) }}
                >
                  🧪 Test
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {logEntry && (
        <LogModal entry={logEntry} token={token} onClose={() => setLogEntry(null)} />
      )}
      {testEntry && (
        <TestModal entry={testEntry} token={token} onClose={() => { setTestEntry(null); load(); }} />
      )}
    </div>
  );
}
